import React from 'react';
import { Cell } from 'react-flexr';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import Grid from '@mui/material/Grid';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import Button from '../common/button';
import Alert from 'app/common/alert';
import { useQuery } from '../hooks/useQuery';
import { useMutation } from '../hooks/useMutation';
import Chip from '../common/chip';

function makeid(length) {
  var result = '';
  var characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  var charactersLength = characters.length;

  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  return result;
}

const DISCOUNTS = `
query GetDiscounts {
    discounts {
        code
        flat
        percentage
        user
        start
        listingType
        end
        status
    }
}
`;

const CREATE_DISCOUNT = `
mutation CreateDiscount($input: DiscountInput!) {
    createDiscount(input: $input){
        code
        flat
        percentage
        user
        start
        end
    }
}
`;

const fallbackI18n = (text) => text;

const formatDate = (value) => {
  if (!value || typeof value !== 'string') {
    return '-';
  }

  return value.replace(/T[0-9:.]+Z/, '');
};

const formatDiscount = (item) => {
  const flat = Number(item && item.flat);
  const percentage = Number(item && item.percentage);

  if (flat > 0) {
    return flat + ' kr.';
  }

  if (percentage > 0) {
    return Math.round((1 - percentage) * 100) + '%';
  }

  return '-';
};

const DiscountDashboard = (props = {}) => {
  const i18n = props.i18n || fallbackI18n;

  const { mutate } = useMutation(CREATE_DISCOUNT);
  const [isAdding, setIsAdding] = React.useState(false);
  const [code, setCode] = React.useState('');
  const [flat, setFlat] = React.useState(0);
  const [percentage, setPercentage] = React.useState(0);
  const [user, setUser] = React.useState('');
  const [listingType, setListingType] = React.useState('');
  const [start, setStart] = React.useState('');
  const [end, setEnd] = React.useState('');
  const [error, setError] = React.useState('');

  const { data, refetch } = useQuery(DISCOUNTS, {
    variables: {}
  });

  const discounts = data && Array.isArray(data.discounts) ? data.discounts : [];

  const onSubmit = () => {
    if (!code) {
      setError('Missing discount code');
      return;
    }

    const flatNumber = Number(flat);
    const percentageNumber = Number(percentage);

    if (!flatNumber && !percentageNumber) {
      setError('Missing flat discount or percentage discount');
      return;
    }

    if (flatNumber && percentageNumber) {
      setError('Discount can only be flat discount or percentage discount');
      return;
    }

    if (!start || !end) {
      setError('Missing start date and/or end date');
      return;
    }

    setError('');

    const discount = {
      code,
      flat: flatNumber,
      percentage:
        percentageNumber >= 1 ? 1 - percentageNumber / 100 : percentageNumber,
      user: user === '' ? null : user,
      start: new Date(start),
      listingType: listingType || null,
      end: new Date(end)
    };

    mutate({ input: discount }).then(() => refetch());
    setIsAdding(false);
  };

  return (
    <div>
      <Paper sx={{ width: '100%', overflowX: 'auto' }}>
        {isAdding ? (
          <Grid style={{ margin: '12px' }}>
            <Cell size="1/2" palm="1/1" style={{ display: 'flex' }}>
              <label style={{ marginRight: '5px', width: 'inherit' }}>
                Discount Code
              </label>
              <TextField
                fullWidth
                name="code"
                onChange={(e) => setCode(e.target.value)}
                required
                value={code}
                type="text"
              />
            </Cell>

            <Cell size="1/2" palm="1/1" style={{ display: 'flex' }}>
              <label style={{ marginRight: '5px', width: 'inherit' }}>
                Flat Discount
              </label>
              <TextField
                fullWidth
                name="flat"
                onChange={(e) => setFlat(e.target.value)}
                required
                value={flat}
                type="number"
              />
            </Cell>

            <Cell size="1/2" palm="1/1" style={{ display: 'flex' }}>
              <label style={{ marginRight: '5px', width: 'inherit' }}>
                Percentage Discount
              </label>
              <TextField
                fullWidth
                name="percentage"
                onChange={(e) => setPercentage(e.target.value)}
                required
                value={percentage}
                type="number"
              />
            </Cell>

            <Cell size="1/2" palm="1/1" style={{ display: 'flex' }}>
              <label style={{ marginRight: '5px', width: 'inherit' }}>
                ListingType
              </label>
              <Select
                displayEmpty
                fullWidth
                onChange={(e) => setListingType(e.target.value)}
                name="listingType"
                value={listingType}
              >
                <MenuItem value="">{i18n('All')}</MenuItem>
                <MenuItem value="PLUS">{i18n('Plus')}</MenuItem>
                <MenuItem value="PREMIUM">{i18n('Premium')}</MenuItem>
              </Select>
            </Cell>

            <Cell size="1/2" palm="1/1" style={{ display: 'flex' }}>
              <label style={{ marginRight: '5px', width: 'inherit' }}>
                User (Optional)
              </label>
              <TextField
                fullWidth
                name="user"
                onChange={(e) => setUser(e.target.value)}
                required
                value={user}
                type="text"
              />
            </Cell>

            <Cell size="1/2" palm="1/1" style={{ display: 'flex' }}>
              <label style={{ marginRight: '5px', width: 'inherit' }}>
                Start Date
              </label>
              <TextField
                fullWidth
                name="start"
                onChange={(e) => setStart(e.target.value)}
                required
                value={start}
                type="date"
              />
            </Cell>

            <Cell size="1/2" palm="1/1" style={{ display: 'flex' }}>
              <label style={{ marginRight: '5px', width: 'inherit' }}>
                End Date
              </label>
              <TextField
                fullWidth
                name="end"
                onChange={(e) => setEnd(e.target.value)}
                required
                value={end}
                type="date"
              />
            </Cell>

            {error ? (
              <Cell size="1/2" palm="1/1" style={{ display: 'flex' }}>
                <Alert type="danger" text={error} />
              </Cell>
            ) : null}

            <Cell size="1/2" palm="1/1" style={{ display: 'flex' }}>
              <Button
                style={{ margin: '12px' }}
                key="submit"
                primary
                label={i18n('Submit')}
                onClick={onSubmit}
              />
              <Button
                style={{ margin: '12px' }}
                key="generate"
                primary
                label={i18n('Generate Random Code')}
                onClick={() => setCode(makeid(8))}
              />
              <Button
                style={{ margin: '12px' }}
                key="cancel"
                danger
                label={i18n('Cancel')}
                onClick={() => setIsAdding(false)}
              />
            </Cell>
          </Grid>
        ) : (
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>{i18n('Code')}</TableCell>
                <TableCell>{i18n('Discount')}</TableCell>
                <TableCell>{i18n('Status')}</TableCell>
                <TableCell>{i18n('Listing Type')}</TableCell>
                <TableCell>{i18n('User')}</TableCell>
                <TableCell>{i18n('Start')}</TableCell>
                <TableCell>{i18n('End')}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              <TableRow key="add-new">
                <TableCell colSpan={7}>
                  <Button
                    style={{ margin: '12px' }}
                    key="add"
                    primary
                    label={i18n('Add new discount code')}
                    onClick={() => setIsAdding(true)}
                  />
                </TableCell>
              </TableRow>

              {discounts.map((item = {}, index) => {
                const status = item.status || 'unknown';

                return (
                  <TableRow key={item.code || index} style={{ margin: '5px' }}>
                    <TableCell>{item.code || '-'}</TableCell>
                    <TableCell>{formatDiscount(item)}</TableCell>
                    <TableCell>
                      <Chip template={status.toLowerCase()} disableAvatar />
                    </TableCell>
                    <TableCell>{item.listingType || 'ALL'}</TableCell>
                    <TableCell>{item.user || '-'}</TableCell>
                    <TableCell>{formatDate(item.start)}</TableCell>
                    <TableCell>{formatDate(item.end)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>
    </div>
  );
};

export default DiscountDashboard;
